/**
 * Position types for the People tab, and the palette every People surface
 * (control rail, headcount ramp, composition split, positions table, org chart
 * nodes, org chart filter legend, org chart PDF key) colours by.
 *
 * Every colour below is an existing `tailwind.config.js` token, so the People tab
 * introduces no new design tokens:
 *
 *   Paid       #055DF5  blue.DEFAULT
 *   Secondee   #1B1C1D  charcoal.DEFAULT
 *   Consultant #4485F8  blue.75
 *   Volunteer  #00DD8D  green.DEFAULT
 *
 * red.DEFAULT (#F00024) is deliberately absent: red is reserved for alerts
 * (behind-schedule tags, adverse deltas) and must never encode a position type,
 * or a legend swatch and a warning become indistinguishable.
 */

export const TYPE_PAID = 'P';
export const TYPE_SECONDEE = 'S';
export const TYPE_CONSULTANT = 'C';
export const TYPE_VOLUNTEER = 'V';

/** Display order, used for legends, stacked series and row groups alike. */
export const POSITION_TYPE_ORDER = [TYPE_PAID, TYPE_SECONDEE, TYPE_CONSULTANT, TYPE_VOLUNTEER];

export const POSITION_TYPE_COLOURS = {
    [TYPE_PAID]: '#055DF5',
    [TYPE_SECONDEE]: '#1B1C1D',
    [TYPE_CONSULTANT]: '#4485F8',
    [TYPE_VOLUNTEER]: '#00DD8D',
};

/**
 * The org chart's OrgChart template tag for an untyped position — a position
 * whose `type` matches none of the four above.
 */
export const ORGANIGRAM_UNTYPED_TAG = 'unknown';

/**
 * Type code → the org chart's OrgChart template tag. Paid nodes are tagged
 * `grey` rather than `paid`; the rest match their type name.
 */
export const TYPE_TO_ORGANIGRAM_TAG = {
    [TYPE_PAID]: 'grey',
    [TYPE_SECONDEE]: 'secondee',
    [TYPE_CONSULTANT]: 'consultant',
    [TYPE_VOLUNTEER]: 'volunteer',
};

/**
 * OrgChart template tag → the node's left accent bar colour. This is the org
 * chart's single colour source: the node templates, the filter legend swatches
 * and the PDF export key all read it, so swapping this one map re-colours the
 * whole chart and everything that explains it.
 *
 * `unknown` uses charcoal.25 (#C6C6C7) — a neutral that reads as "no type" and
 * cannot be mistaken for one of the four.
 */
export const ORGANIGRAM_TAG_COLOURS = {
    [TYPE_TO_ORGANIGRAM_TAG[TYPE_PAID]]: POSITION_TYPE_COLOURS[TYPE_PAID],
    [TYPE_TO_ORGANIGRAM_TAG[TYPE_SECONDEE]]: POSITION_TYPE_COLOURS[TYPE_SECONDEE],
    [TYPE_TO_ORGANIGRAM_TAG[TYPE_CONSULTANT]]: POSITION_TYPE_COLOURS[TYPE_CONSULTANT],
    [TYPE_TO_ORGANIGRAM_TAG[TYPE_VOLUNTEER]]: POSITION_TYPE_COLOURS[TYPE_VOLUNTEER],
    [ORGANIGRAM_UNTYPED_TAG]: '#C6C6C7',
};

/** The committed indicator dot — charcoal.DEFAULT, matching the card's title. */
export const ORGANIGRAM_COMMITTED_COLOUR = '#1B1C1D';

/**
 * The behind-schedule mark — red.DEFAULT (#F00024).
 *
 * The one place red belongs on this chart, and the reason the note at the top
 * of this file holds: no position type is red, so a red date line cannot be
 * mistaken for a type. Behind schedule and committed are mutually exclusive by
 * definition, so no card ever carries both marks.
 */
export const ORGANIGRAM_BEHIND_SCHEDULE_COLOUR = '#F00024';

/**
 * The behind-schedule warning glyph, as the contents of a 10×10 SVG box.
 *
 * Returned as markup rather than drawn per caller because its two consumers
 * are string contexts the chart library and the PDF exporter assemble
 * themselves — a node template and the exported document's key — neither of
 * which can render a React component. One definition, so the mark beside a
 * card's dates and the mark in the key that explains it cannot diverge.
 */
export function organigramBehindScheduleGlyph(colour = ORGANIGRAM_BEHIND_SCHEDULE_COLOUR) {
    return (
        `<path d="M5,0 L10,10 L0,10 Z" fill="${colour}"></path>` +
        `<rect x="4.4" y="3.6" width="1.2" height="3.3" fill="#FFFFFF"></rect>` +
        `<rect x="4.4" y="7.7" width="1.2" height="1.2" fill="#FFFFFF"></rect>`
    );
}

/**
 * Type code → the key `WorkforceTypeFilterPanel` (and the `organigram_filter`
 * preference) uses. The panel predates the People tab and is keyed by name.
 */
export const TYPE_TO_FILTER_KEY = {
    [TYPE_PAID]: 'paid',
    [TYPE_SECONDEE]: 'secondee',
    [TYPE_CONSULTANT]: 'consultant',
    [TYPE_VOLUNTEER]: 'volunteer',
};

/**
 * Labels are resolved lazily. `window.$t` is installed by app.js before any
 * component is required, but i18next resources can still be loading, so calling
 * it at module scope would freeze the English fallback into the bundle.
 */
export function positionTypeLabel(type) {
    switch (type) {
        case TYPE_PAID:
            return $t('Paid');
        case TYPE_SECONDEE:
            return $t('Secondee');
        case TYPE_CONSULTANT:
            return $t('Consultant');
        case TYPE_VOLUNTEER:
            return $t('Volunteer');
        default:
            return type;
    }
}

/** `['P', 'V']` → `{ paid: true, secondee: false, consultant: false, volunteer: true }` */
export function typesToFilterState(types) {
    const selected = Array.isArray(types) ? types : [];

    return POSITION_TYPE_ORDER.reduce((carry, type) => {
        carry[TYPE_TO_FILTER_KEY[type]] = selected.includes(type);

        return carry;
    }, {});
}

/** Inverse of `typesToFilterState`, always returned in `POSITION_TYPE_ORDER`. */
export function filterStateToTypes(filterState) {
    return POSITION_TYPE_ORDER.filter((type) => filterState[TYPE_TO_FILTER_KEY[type]] === true);
}
